import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Switch,
  Table,
  Space,
  Modal,
  message,
  Tag,
  Popconfirm,
  Select,
  Badge
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  MailOutlined,
  SendOutlined,
  EyeOutlined
} from '@ant-design/icons';
import {
  emailConfigService,
  EmailSender,
  EmailRecipient,
  EmailSendLog
} from '../services/emailConfigService';
import TimeDisplay from './TimeDisplay';
import { useI18n } from '../hooks/useI18n';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const EmailConfigManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState('sender');
  const [senderForm] = Form.useForm();
  const [recipientForm] = Form.useForm();
  
  // 发件人配置
  const [senderConfig, setSenderConfig] = useState<EmailSender | null>(null);
  const [senderLoading, setSenderLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [senderDetailModalVisible, setSenderDetailModalVisible] = useState(false);
  const [senderEditModalVisible, setSenderEditModalVisible] = useState(false);

  // 收件人管理
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientModalVisible, setRecipientModalVisible] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<EmailRecipient | null>(null);
  const [recipientTypeFilter, setRecipientTypeFilter] = useState<string | undefined>();

  // 发送记录
  const [sendLogs, setSendLogs] = useState<EmailSendLog[]>([]);
  const [sendLogsLoading, setSendLogsLoading] = useState(false);
  const [sendLogsPagination, setSendLogsPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });

  const { t } = useI18n();

  const RECIPIENT_TYPES = [
    { value: 'health_check', label: t('emailConfig.healthCheck') },
    { value: 'alert', label: t('emailConfig.alert') },
    { value: 'notification', label: t('emailConfig.notification') }
  ];

  useEffect(() => {
    loadSenderConfig();
    loadRecipients();
    loadSendLogs();
  }, []);

  useEffect(() => {
    loadRecipients();
  }, [recipientTypeFilter]);

  useEffect(() => {
    loadSendLogs();
  }, [sendLogsPagination.current, sendLogsPagination.pageSize]);

  // 发件人配置相关
  const loadSenderConfig = async () => {
    setSenderLoading(true);
    try {
      const result = await emailConfigService.getSender();
      if (result.success && result.data) {
        setSenderConfig(result.data);
        senderForm.setFieldsValue(result.data);
      }
    } catch (error: any) {
      message.error(t('emailConfig.loadSenderFailed') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setSenderLoading(false);
    }
  };

  const saveSenderConfig = async (values: any) => {
    setSenderLoading(true);
    try {
      // 如果是编辑模式且密码为空，则不发送密码字段
      const dataToSave = { ...values };
      if (senderConfig && !dataToSave.smtp_pass) {
        delete dataToSave.smtp_pass;
      }
      
      const result = await emailConfigService.saveSender(dataToSave);
      if (result.success) {
        message.success(t('emailConfig.saveSenderSuccess'));
        setSenderEditModalVisible(false);
        await loadSenderConfig();
      } else {
        message.error(result.error || t('common.error'));
      }
    } catch (error: any) {
      message.error(t('emailConfig.saveSenderFailed') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setSenderLoading(false);
    }
  };

  const handleViewSenderDetails = () => {
    setSenderDetailModalVisible(true);
  };

  const handleEditSender = () => {
    if (senderConfig) {
      const formValues = { ...senderConfig };
      // 编辑时不填充密码字段，让用户选择是否修改
      delete formValues.smtp_pass;
      senderForm.setFieldsValue(formValues);
    }
    setSenderEditModalVisible(true);
  };

  const handleAddSender = () => {
    senderForm.resetFields();
    senderForm.setFieldsValue({
      smtp_port: 587,
      smtp_secure: false
    });
    setSenderEditModalVisible(true);
  };

  const testSenderConfig = async () => {
    if (!testEmail) {
      message.warning(t('emailConfig.testEmailWarning'));
      return;
    }
    setTestLoading(true);
    try {
      const result = await emailConfigService.testSender(testEmail);
      if (result.success) {
        message.success(t('emailConfig.testEmailSuccess'));
      } else {
        message.error(result.error || t('emailConfig.testEmailFailed'));
      }
    } catch (error: any) {
      message.error(t('emailConfig.testEmailFailed') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setTestLoading(false);
    }
  };

  // 收件人管理相关
  const loadRecipients = async () => {
    setRecipientLoading(true);
    try {
      const result = await emailConfigService.getRecipients(recipientTypeFilter);
      if (result.success && result.data) {
        setRecipients(result.data);
      }
    } catch (error: any) {
      message.error(t('emailConfig.loadRecipientsFailed') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setRecipientLoading(false);
    }
  };

  const handleAddRecipient = () => {
    setEditingRecipient(null);
    recipientForm.resetFields();
    setRecipientModalVisible(true);
  };

  const handleEditRecipient = (record: EmailRecipient) => {
    setEditingRecipient(record);
    recipientForm.setFieldsValue({
      ...record,
      recipient_types: record.recipient_types ? record.recipient_types.split(',') : []
    });
    setRecipientModalVisible(true);
  };

  const saveRecipient = async (values: any) => {
    try {
      const recipientData = {
        ...values,
        recipient_types: Array.isArray(values.recipient_types)
          ? values.recipient_types.join(',')
          : values.recipient_types || ''
      };

      if (editingRecipient) {
        const result = await emailConfigService.updateRecipient(editingRecipient.id!, recipientData);
        if (result.success) {
          message.success(t('emailConfig.recipientUpdated'));
          setRecipientModalVisible(false);
          await loadRecipients();
        } else {
          message.error(result.error || t('common.error'));
        }
      } else {
        const result = await emailConfigService.addRecipient(recipientData);
        if (result.success) {
          message.success(t('emailConfig.recipientAdded'));
          setRecipientModalVisible(false);
          await loadRecipients();
        } else {
          message.error(result.error || t('common.error'));
        }
      }
    } catch (error: any) {
      message.error(t('emailConfig.saveRecipientFailed') + ': ' + (error.message || t('common.unknownError')));
    }
  };

  const handleDeleteRecipient = async (id: number) => {
    try {
      const result = await emailConfigService.deleteRecipient(id);
      if (result.success) {
        message.success(t('emailConfig.recipientDeleted'));
        await loadRecipients();
      } else {
        message.error(result.error || t('common.error'));
      }
    } catch (error: any) {
      message.error(t('emailConfig.deleteRecipientFailed') + ': ' + (error.message || t('common.unknownError')));
    }
  };

  // 发送记录相关
  const loadSendLogs = async () => {
    setSendLogsLoading(true);
    try {
      const result = await emailConfigService.getSendLogs({
        page: sendLogsPagination.current,
        limit: sendLogsPagination.pageSize
      });
      if (result.success) {
        setSendLogs(result.data);
        setSendLogsPagination(prev => ({
          ...prev,
          total: result.total
        }));
      }
    } catch (error: any) {
      message.error(t('emailConfig.loadSendLogsFailed') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setSendLogsLoading(false);
    }
  };

  const recipientColumns = [
    {
      title: t('emailConfig.emailAddress'),
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: t('emailConfig.recipientType'),
      dataIndex: 'recipient_types',
      key: 'recipient_types',
      render: (types: string) => {
        if (!types) return '-';
        return types.split(',').map((type: string) => {
          const typeInfo = RECIPIENT_TYPES.find(rt => rt.value === type.trim());
          return (
            <Tag key={type} color="blue">
              {typeInfo ? typeInfo.label : type.trim()}
            </Tag>
          );
        });
      }
    },
    {
      title: t('emailConfig.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: t('emailConfig.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Badge status={active ? 'success' : 'default'} text={active ? t('emailConfig.enabled') : t('emailConfig.disabled')} />
      )
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_: any, record: EmailRecipient) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditRecipient(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('emailConfig.confirmDeleteRecipient')}
            onConfirm={() => handleDeleteRecipient(record.id!)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const sendLogsColumns = [
    {
      title: t('emailConfig.recipient'),
      dataIndex: 'recipient_email',
      key: 'recipient_email'
    },
    {
      title: t('emailConfig.recipientType'),
      dataIndex: 'recipient_types',
      key: 'recipient_types',
      render: (types: string) => {
        if (!types) return '-';
        return types.split(',').map((type: string) => {
          const typeInfo = RECIPIENT_TYPES.find(rt => rt.value === type.trim());
          return (
            <Tag key={type} color="blue">
              {typeInfo ? typeInfo.label : type.trim()}
            </Tag>
          );
        });
      }
    },
    {
      title: t('emailConfig.subject'),
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true
    },
    {
      title: t('emailConfig.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        switch (status) {
          case 'success':
            return <Badge status="success" text={t('emailConfig.success')} />;
          case 'failed':
            return <Badge status="error" text={t('emailConfig.failed')} />;
          default:
            return <Badge status="default" text={t('emailConfig.pending')} />;
        }
      }
    },
    {
      title: t('emailConfig.errorMessage'),
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: t('emailConfig.sendTime'),
      dataIndex: 'sent_at',
      key: 'sent_at',
      render: (time: string) => time ? <TimeDisplay utcTime={time} format="datetime" /> : '-'
    },
    {
      title: t('emailConfig.createTime'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => <TimeDisplay utcTime={time} format="datetime" />
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* 发件人配置标签页 */}
          <TabPane tab={<span><MailOutlined />{t('emailConfig.senderConfig')}</span>} key="sender">
            <Card>
              {senderConfig ? (
                <>
                  <Card 
                    type="inner" 
                    title={t('emailConfig.senderInfo')}
                    extra={
                      <Space>
                        <Button 
                          icon={<EyeOutlined />} 
                          onClick={handleViewSenderDetails}
                        >
                          {t('emailConfig.viewDetails')}
                        </Button>
                        <Button 
                          type="primary" 
                          icon={<EditOutlined />} 
                          onClick={handleEditSender}
                        >
                          {t('emailConfig.modify')}
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={loadSenderConfig}>
                          {t('emailConfig.refresh')}
                        </Button>
                      </Space>
                    }
                  >
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <div>
                        <strong>{t('emailConfig.smtpHost')}:</strong> {senderConfig.smtp_host || '-'}
                      </div>
                      <div>
                        <strong>{t('emailConfig.smtpPort')}:</strong> {senderConfig.smtp_port || '-'}
                      </div>
                      <div>
                        <strong>{t('emailConfig.useSslTls')}:</strong> {senderConfig.smtp_secure ? t('common.yes') : t('common.no')}
                      </div>
                      <div>
                        <strong>{t('emailConfig.username')}:</strong> {senderConfig.smtp_user || '-'}
                      </div>
                      <div>
                        <strong>{t('emailConfig.senderAddress')}:</strong> {senderConfig.smtp_from || senderConfig.smtp_user || '-'}
                      </div>
                    </Space>
                  </Card>

                  <Card size="small" title={t('emailConfig.testEmail')} style={{ marginTop: 24 }}>
                    <Space>
                      <Input
                        placeholder={t('emailConfig.testEmailPlaceholder')}
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        style={{ width: 300 }}
                      />
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        loading={testLoading}
                        onClick={testSenderConfig}
                      >
                        {t('emailConfig.sendTestEmail')}
                      </Button>
                    </Space>
                  </Card>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <MailOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                  <div style={{ fontSize: 16, color: '#999', marginBottom: 24 }}>
                    {t('emailConfig.noSenderConfig')}
                  </div>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    size="large"
                    onClick={handleAddSender}
                  >
                    {t('emailConfig.addSender')}
                  </Button>
                </div>
              )}
            </Card>
          </TabPane>

          {/* 收件人管理标签页 */}
          <TabPane tab={<span><MailOutlined />{t('emailConfig.recipientManagement')}</span>} key="recipients">
            <Card>
              <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Space>
                  <Select
                    placeholder={t('emailConfig.filterByType')}
                    allowClear
                    style={{ width: 200 }}
                    value={recipientTypeFilter}
                    onChange={setRecipientTypeFilter}
                  >
                    {RECIPIENT_TYPES.map(type => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                  <Button icon={<ReloadOutlined />} onClick={loadRecipients}>
                    {t('emailConfig.refresh')}
                  </Button>
                </Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddRecipient}
                >
                  {t('emailConfig.addRecipient')}
                </Button>
              </Space>

              <Table
                columns={recipientColumns}
                dataSource={recipients}
                loading={recipientLoading}
                rowKey="id"
                pagination={false}
              />
            </Card>
          </TabPane>

          {/* 发送记录标签页 */}
          <TabPane tab={<span><SendOutlined />{t('emailConfig.sendLogs')}</span>} key="logs">
            <Card>
              <Table
                columns={sendLogsColumns}
                dataSource={sendLogs}
                loading={sendLogsLoading}
                rowKey="id"
                pagination={{
                  current: sendLogsPagination.current,
                  pageSize: sendLogsPagination.pageSize,
                  total: sendLogsPagination.total,
                  showSizeChanger: true,
                  showTotal: (total) => `${t('common.total')} ${total} ${t('common.items')}`,
                  onChange: (page, pageSize) => {
                    setSendLogsPagination(prev => ({
                      ...prev,
                      current: page,
                      pageSize: pageSize || 50
                    }));
                  }
                }}
              />
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {/* 发件人详情模态框 */}
      <Modal
        title={t('emailConfig.senderDetails')}
        open={senderDetailModalVisible}
        onCancel={() => setSenderDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSenderDetailModalVisible(false)}>
            {t('common.close')}
          </Button>,
          <Button key="edit" type="primary" onClick={() => {
            setSenderDetailModalVisible(false);
            handleEditSender();
          }}>
            {t('emailConfig.modify')}
          </Button>
        ]}
        width={600}
      >
        {senderConfig && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <strong>{t('emailConfig.smtpHost')}:</strong>
              <div style={{ marginTop: 8 }}>{senderConfig.smtp_host || '-'}</div>
            </div>
            <div>
              <strong>{t('emailConfig.smtpPort')}:</strong>
              <div style={{ marginTop: 8 }}>{senderConfig.smtp_port || '-'}</div>
            </div>
            <div>
              <strong>{t('emailConfig.useSslTls')}:</strong>
              <div style={{ marginTop: 8 }}>{senderConfig.smtp_secure ? t('common.yes') : t('common.no')}</div>
            </div>
            <div>
              <strong>{t('emailConfig.username')}:</strong>
              <div style={{ marginTop: 8 }}>{senderConfig.smtp_user || '-'}</div>
            </div>
            <div>
              <strong>{t('emailConfig.password')}:</strong>
              <div style={{ marginTop: 8 }}>••••••••</div>
            </div>
            <div>
              <strong>{t('emailConfig.senderAddress')}:</strong>
              <div style={{ marginTop: 8 }}>{senderConfig.smtp_from || senderConfig.smtp_user || '-'}</div>
            </div>
          </Space>
        )}
      </Modal>

      {/* 发件人编辑模态框 */}
      <Modal
        title={senderConfig ? t('emailConfig.editSender') : t('emailConfig.addSender')}
        open={senderEditModalVisible}
        onCancel={() => setSenderEditModalVisible(false)}
        onOk={() => senderForm.submit()}
        width={600}
        confirmLoading={senderLoading}
      >
        <Form
          form={senderForm}
          layout="vertical"
          onFinish={saveSenderConfig}
          initialValues={{
            smtp_port: 587,
            smtp_secure: false
          }}
        >
          <Form.Item
            label={t('emailConfig.smtpHost')}
            name="smtp_host"
            rules={[{ required: true, message: t('emailConfig.smtpHostRequired') }]}
          >
            <Input placeholder={t('emailConfig.smtpHostPlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('emailConfig.smtpPort')}
            name="smtp_port"
            rules={[{ required: true, message: t('emailConfig.smtpPortRequired') }]}
          >
            <Input type="number" placeholder={t('emailConfig.smtpPortPlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('emailConfig.useSslTls')}
            name="smtp_secure"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={t('emailConfig.username')}
            name="smtp_user"
            rules={[{ required: true, message: t('emailConfig.usernameRequired') }]}
          >
            <Input placeholder={t('emailConfig.smtpUserPlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('emailConfig.password')}
            name="smtp_pass"
            rules={senderConfig ? [] : [{ required: true, message: t('emailConfig.passwordRequired') }]}
          >
            <Input.Password placeholder={senderConfig ? t('emailConfig.smtpPassPlaceholderEdit') : t('emailConfig.smtpPassPlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('emailConfig.senderAddress')}
            name="smtp_from"
          >
            <Input placeholder={t('emailConfig.smtpFromPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 收件人编辑模态框 */}
      <Modal
        title={editingRecipient ? t('emailConfig.editRecipient') : t('emailConfig.addRecipient')}
        open={recipientModalVisible}
        onCancel={() => setRecipientModalVisible(false)}
        onOk={() => recipientForm.submit()}
        width={600}
      >
        <Form
          form={recipientForm}
          layout="vertical"
          onFinish={saveRecipient}
        >
          <Form.Item
            label={t('emailConfig.emailAddress')}
            name="email"
            rules={[
              { required: true, message: t('emailConfig.emailRequired') },
              { type: 'email', message: t('emailConfig.emailInvalid') }
            ]}
          >
            <Input placeholder={t('emailConfig.emailPlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('emailConfig.recipientType')}
            name="recipient_types"
            rules={[{ required: true, message: t('emailConfig.selectAtLeastOneType') }]}
          >
            <Select
              mode="multiple"
              placeholder={t('emailConfig.selectRecipientType')}
              options={RECIPIENT_TYPES}
            />
          </Form.Item>

          <Form.Item
            label={t('emailConfig.description')}
            name="description"
          >
            <TextArea rows={3} placeholder={t('emailConfig.recipientDescriptionPlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('emailConfig.enableStatus')}
            name="is_active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailConfigManager;

